/**
 * Backend selection (runtime switchable in production builds).
 *
 * Reads the user's choice from localStorage once at module load. Changing the
 * selection in Settings → Connection writes to localStorage and reloads the
 * page so the new URL takes effect across all services.
 *
 * In dev mode (`import.meta.env.DEV`) requests go through the Vite proxy,
 * which exposes every backend under its own `/be-<id>` prefix (see
 * vite.config.ts). The selector therefore works in dev too — the reload below
 * is enough, with no Vite restart. Direct calls are not an option: the
 * backends allow-list CORS origins, and localhost is not on the list.
 */

export type BackendId = 'local' | 'dev' | 'production'

export interface BackendOption {
  id: BackendId
  label: string
  url: string
}

/**
 * Local mode — enabled with `./start.sh local` (or VITE_LOCAL_MODE=true). When
 * on, the local backend (docker-compose.local.yml, default localhost:8080) is
 * offered in Settings → Connection and selected by default, the Connection
 * picker is ungated, and a "Local" AI model appears in the model selector.
 */
export const LOCAL_MODE = import.meta.env.VITE_LOCAL_MODE === 'true'

const LOCAL_BACKEND: BackendOption = {
  id: 'local',
  label: 'Local (docker-compose.local.yml)',
  // VITE_API_BASE_URL doubles as the Vite proxy target in dev (see vite.config.ts).
  url: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
}

export const BACKENDS: BackendOption[] = [
  ...(LOCAL_MODE ? [LOCAL_BACKEND] : []),
  { id: 'dev',        label: 'Dev (pipar-dev.piparapp.com)', url: 'https://pipar-dev.piparapp.com' },
  { id: 'production', label: 'Production (ginger.piparapp.com)', url: 'https://ginger.piparapp.com' },
]

const STORAGE_KEY = 'pipar_backend'

function readStoredBackend(): BackendId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'local' && LOCAL_MODE) return 'local'
    if (raw === 'dev' || raw === 'production') return raw
  } catch {
    // localStorage unavailable (private mode / embedded contexts) — fall through
  }
  // In local mode default to the local backend; otherwise production.
  return LOCAL_MODE ? 'local' : 'production'
}

export function getSelectedBackend(): BackendId {
  return readStoredBackend()
}

export function getSelectedBackendOption(): BackendOption {
  const id = readStoredBackend()
  return BACKENDS.find((b) => b.id === id) ?? BACKENDS[0]
}

/** Persist the user's choice and hard-reload so every service picks up the new URL. */
export function setSelectedBackend(id: BackendId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // ignore — best effort
  }
  // All API_BASE values are computed at module load, so a reload is required.
  window.location.reload()
}

/**
 * Force non-owner users off the dev backend. The Connection picker is already
 * owner-gated in Settings, but a user that previously selected `dev` (as an
 * owner) and later lost that tier would otherwise stay on the dev backend
 * indefinitely. Direct localStorage edits also bypass the picker. Both cases
 * are handled here: when we have a definitive non-owner tier and `dev` is
 * stored, force-switch to `production` (which triggers a reload).
 *
 * Pass `null` while the tier is still loading — this function no-ops until a
 * definitive answer is available so we don't bounce users on every cold start.
 */
export function enforceBackendAccess(subscriptionTier: string | null): void {
  // Local mode pins itself to the local backend regardless of subscription tier.
  if (LOCAL_MODE) return
  if (subscriptionTier === null) return
  if (subscriptionTier === 'owner') return
  if (getSelectedBackend() === 'production') return
  setSelectedBackend('production')
}

/** Computed once at module load. All services read this constant. */
export const API_BASE: string = import.meta.env.DEV
  // Dev: through the Vite proxy, under the selected backend's prefix.
  ? `/be-${readStoredBackend()}`
  : (getSelectedBackendOption().url || import.meta.env.VITE_API_BASE_URL || 'https://ginger.piparapp.com')
